import './ProductEditForm.css';

import { useEffect, useState } from 'react';
import B2BFormContainer from '../../b2b-common/B2BFormContainer';
import LabelTranslated, { setFieldAndLabelField } from '../../b2b-languages/components/LabelTranslated';
import { useLoginContext } from '../../../App';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import { Box } from '@mui/material';
import ProductsDropdown from '../components/products/ProductsDropdown';
import SwitchWithTooltip from '../../b2b-common/SwitchWithTooltip';
import EditText from '../../b2b-common/EditText';
import { showNotification } from '../../b2b-common/Notification';
import { addProduct, deleteProduct, removeProductImage, updateProduct, uploadProductImage } from '../../../services/b2b-users-apiService';
import { useContentContext } from '../../layout/Layout';
import B2BImageCard from '../../b2b-common/B2BImageCard';

const ProductEditForm = ({ product, showForm = false, onCancel, onSave, onDelete }) => {

    const { loginData } = useLoginContext();
    const { t } = useB2BTranslation('b2b-users');
    const { setNotification } = useContentContext();

    const [currentProduct, setCurrentProduct] = useState(null);
    const [productImage, setProductImage] = useState(null);
    const [productImageChanged, setProductImageChanged] = useState(false);
    const [deleteProductImage, setDeleteProductImage] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (product) {
            setCurrentProduct(product);
        } else {
            setCurrentProduct({
                productId: 0,
                parentProductId: 1,
                productAcronym: 'B2B_NEW_PRODUCT_ACRONYM',
                productName_label: '{b2b.product.new_product.name}',
                productName: '{b2b.product.new_product.name}',
                productDescription_label: '{b2b.product.new_product.description}',
                productDescription: '{b2b.product.new_product.description}',
                productParameterized: false,
                productEnabled: true,
            });
        }
    }, [product]);

    const handleSave = async () => {
        let productToSave = { ...currentProduct };
        productToSave.productName = productToSave.productName_label;
        productToSave.productDescription = productToSave.productDescription_label;
        if (currentProduct.productId === 0) { // new product
            try {
                setIsSaving(true);
                const newProduct = await addProduct(loginData.accessToken, productToSave)
                if (productImage) {
                    const response = await uploadProductImage(loginData.accessToken, newProduct.productId, productImage);
                }
                showNotification(setNotification, `${t('New product created: ')} ${newProduct.productId}`, 'success', 3000);
                onSave && onSave(newProduct);
            } catch (error) {
                console.error('Error saving product:', error);
                showNotification(setNotification, `${t('Error saving product: ')} ${error}`, 'error', 5000);
            } finally {
                setIsSaving(false);
            }
        } else { // update product
            try {
                setIsSaving(true);
                const updatedProduct = await updateProduct(loginData.accessToken, productToSave);
                if (productImage && productImageChanged) {
                    const response = await uploadProductImage(loginData.accessToken, updatedProduct.productId, productImage);
                } else  if (deleteProductImage) {
                    const response = await removeProductImage(loginData.accessToken, updatedProduct.productId);
                }
                showNotification(setNotification, `${t('Product updated: ')} ${currentProduct.productId}`, 'success', 3000);
                onSave && onSave(currentProduct);
            } catch (error) {
                console.error('Error updating product:', error);
                showNotification(setNotification, `${t('Error updating product: ')} ${error}`, 'error', 5000);
            } finally {
                setIsSaving(false);
            }
        }
    }

    const handleDelete = async () => {
        if (currentProduct.productId !== 0) {
            try {
                if (productImage) {
                    const response = await removeProductImage(loginData.accessToken, currentProduct.productId);
                }
                const response = await deleteProduct(loginData.accessToken, currentProduct.productId);
                showNotification(setNotification, `${t('Product deleted: ')} ${currentProduct.productId}`, 'success', 3000);
                onDelete && onDelete();
            } catch (error) {
                console.error('Error deleting product:', error);
                showNotification(setNotification, `${t('Error deleting product: ')} ${error}`, 'error', 5000);
            }
        }
    }

    const handleCancel = () => {
        onCancel && onCancel();
    }

    if (!showForm || !currentProduct) {
        return null;
    }

    const handleParentProductSelected = (selectedProduct) => {
        // console.log('Selected product:', selectedProduct);
        setCurrentProduct((prevProduct) => ({ ...prevProduct, parentProductId: selectedProduct.productId }));
    };

    return (
        <B2BFormContainer
            buttonsConfig={[
                { label: t('Save'), onClick: handleSave },
                ...(product !== null && product.productId !== 0 ? [{
                    label: t('Delete'),
                    confirm: true,
                    onConfirm: handleDelete,
                }] : []),
                { label: t('Cancel'), className: 'b2b-button-cancel white_03', onClick: handleCancel },
            ]}
            showDivider={false}
            className="product-edit-form b2b-form-01"
        >
            <Box className='product-edit-form-image' display={'flex'} width={'35%'} justifyContent={'center'}>
                <B2BImageCard 
                    imagePath={currentProduct.imagePath}
                    showChangeButton={true}
                    requestChangeConfirmation={true}
                    onChange={(file) => {
                        setProductImage(file);
                        setProductImageChanged(true);
                    }}
                    showDeleteButton={true}
                    requestDeleteConfirmation={true}
                    onDelete={() => {
                        setProductImage(null);
                        setDeleteProductImage(true);
                    }}
                    synchrousUpdate={false}
                    className={'product-image-card'}
                />  
            </Box>
            <Box className='product-edit-form-name-and-description'
                display={'flex'} flexDirection={'column'}
                gap={2} width={'65%'}
            >
                <Box display={'flex'} flexDirection={'row'} gap={2}>
                    <LabelTranslated field={t('Name Label')} labelValue={currentProduct.productName_label}
                        locale={`${t('Name')} - ${loginData.locale}`} translation={currentProduct.productName}
                        onChange={(e) => {
                            console.log('Product name changed:', e);
                            setFieldAndLabelField(currentProduct, setCurrentProduct, 'productName', e.target.value);
                        }}
                    />
                    <SwitchWithTooltip
                        tooltipTitle={t('Product Enabled')}
                        tooltipPlacement='right'
                        checked={currentProduct.productEnabled}
                        onChange={(e) => setCurrentProduct((prevProduct) => ({ ...prevProduct, productEnabled: e.target.checked }))}
                        className={'product-enabled-switch green-red'}
                    />
                </Box>
                <LabelTranslated field={t('Description Label')} labelValue={currentProduct.productDescription_label}
                    locale={`${t('Description')} - ${loginData.locale}`} translation={currentProduct.productDescription}
                    rowsNumber={4}
                    onChange={(e) => {
                        setFieldAndLabelField(currentProduct, setCurrentProduct, 'productDescription', e.target.value);
                    }}

                />
                <EditText object={currentProduct} objectField={'productAcronym'} setObjectField={setCurrentProduct} label={t('Product Acronym')} />
                <Box display={'flex'} flexDirection={'row'} gap={2}>
                    <ProductsDropdown defaultProductId={currentProduct.parentProductId} onProductSelected={handleParentProductSelected} />
                    <SwitchWithTooltip
                        tooltipTitle={t('Product Parameterized')}
                        tooltipPlacement='right'
                        checked={currentProduct.productParameterized}
                        onChange={(e) => setCurrentProduct((prevProduct) => ({ ...prevProduct, productParameterized: e.target.checked }))}
                    />
                </Box>
            </Box>
        </B2BFormContainer>
    );
}

export default ProductEditForm;